import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const FONTS = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Raleway'];

export default function PartnerBrandingSettings() {
  const { t } = useTranslation();
  const TIERS = [
    { value: 'STANDARD', label: t('PartnerBrandingSettings.tierStandardLabel'), desc: t('PartnerBrandingSettings.tierStandardDesc') },
    { value: 'LIGHT',    label: t('PartnerBrandingSettings.tierLightLabel'),    desc: t('PartnerBrandingSettings.tierLightDesc') },
    { value: 'HIDDEN',   label: t('PartnerBrandingSettings.tierHiddenLabel'),   desc: t('PartnerBrandingSettings.tierHiddenDesc') },
  ];
  const [tab, setTab]       = useState('brand');
  const [branding, setBranding] = useState(null);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [subErr, setSubErr] = useState('');

  useEffect(() => {
    axiosInstance.get('/partner/branding').then(r => {
      setBranding(r.data);
      setForm(r.data);
    });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axiosInstance.put('/partner/branding', form);
      setBranding(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const requestSubdomain = async () => {
    setSubErr('');
    if (!subdomain.match(/^[a-z0-9-]+\.[a-z0-9.-]+$/)) {
      setSubErr(t('PartnerBrandingSettings.subdomainInvalid'));
      return;
    }
    await axiosInstance.post('/partner/branding/subdomain', { subdomain });
    setForm(f => ({ ...f, subdomain, subdomainStatus: 'PENDING' }));
    setBranding(b => ({ ...b, subdomain, subdomainStatus: 'PENDING' }));
  };

  if (!branding) return <div className="p-8 text-gray-400">{t('PartnerBrandingSettings.loading')}</div>;

  const previewBg  = form.primaryColor || '#1e3a5f';
  const previewAcc = form.accentColor  || '#3b82f6';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-1">{t('PartnerBrandingSettings.pageTitle')}</h1>
      <p className="text-gray-400 text-sm mb-6">
        {t('PartnerBrandingSettings.pageSubtitle')}
      </p>

      {/* Tier selector */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {TIERS.map(t => (
          <button key={t.value} onClick={() => set('whiteLabelTier', t.value)}
            className={`rounded-xl border p-4 text-left transition-all ${
              form.whiteLabelTier === t.value
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}>
            <p className="text-white font-semibold text-sm">{t.label}</p>
            <p className="text-gray-400 text-xs mt-1">{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {[['brand', t('PartnerBrandingSettings.tabBrandKit')], ['domain', t('PartnerBrandingSettings.tabDomain')], ['email', t('PartnerBrandingSettings.tabEmailSender')]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === id ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: form */}
        <div>
          {tab === 'brand' && (
            <div className="space-y-5">
              <Field label={t('PartnerBrandingSettings.logoLightLabel')}>
                <input value={form.logoUrl || ''} onChange={e => set('logoUrl', e.target.value)}
                  placeholder="https://cdn.yourfirm.com/logo-light.png"
                  className="input-field" />
              </Field>
              <Field label={t('PartnerBrandingSettings.logoDarkLabel')}>
                <input value={form.logoDarkUrl || ''} onChange={e => set('logoDarkUrl', e.target.value)}
                  placeholder="https://cdn.yourfirm.com/logo-dark.png"
                  className="input-field" />
              </Field>
              <Field label={t('PartnerBrandingSettings.faviconLabel')}>
                <input value={form.faviconUrl || ''} onChange={e => set('faviconUrl', e.target.value)}
                  placeholder="https://cdn.yourfirm.com/favicon.ico"
                  className="input-field" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t('PartnerBrandingSettings.primaryColourLabel')}>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={form.primaryColor || '#1e3a5f'}
                      onChange={e => set('primaryColor', e.target.value)}
                      className="h-10 w-14 rounded cursor-pointer bg-transparent border border-white/10" />
                    <input value={form.primaryColor || '#1e3a5f'} onChange={e => set('primaryColor', e.target.value)}
                      className="input-field flex-1" />
                  </div>
                </Field>
                <Field label={t('PartnerBrandingSettings.accentColourLabel')}>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={form.accentColor || '#3b82f6'}
                      onChange={e => set('accentColor', e.target.value)}
                      className="h-10 w-14 rounded cursor-pointer bg-transparent border border-white/10" />
                    <input value={form.accentColor || '#3b82f6'} onChange={e => set('accentColor', e.target.value)}
                      className="input-field flex-1" />
                  </div>
                </Field>
              </div>
              <Field label={t('PartnerBrandingSettings.fontFamilyLabel')}>
                <select value={form.fontFamily || 'Inter'} onChange={e => set('fontFamily', e.target.value)}
                  className="input-field">
                  {FONTS.map(f => <option key={f}>{f}</option>)}
                </select>
              </Field>
              <Field label={t('PartnerBrandingSettings.reportFooterLabel')}>
                <textarea value={form.reportFooterText || ''} rows={2}
                  onChange={e => set('reportFooterText', e.target.value)}
                  placeholder="© 2026 YourFirm Inc. — Confidential"
                  className="input-field resize-none" />
              </Field>
            </div>
          )}

          {tab === 'domain' && (
            <div className="space-y-5">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-sm text-blue-300">
                {t('PartnerBrandingSettings.cnameInstructionBefore')} <code className="bg-white/10 px-1 rounded">app.cataam.com</code>{t('PartnerBrandingSettings.cnameInstructionAfter')}
              </div>
              <Field label={t('PartnerBrandingSettings.customSubdomainLabel')}>
                <div className="flex gap-2">
                  <input value={subdomain} onChange={e => setSubdomain(e.target.value)}
                    placeholder="portal.yourfirm.com" className="input-field flex-1" />
                  <button onClick={requestSubdomain}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg">
                    {t('PartnerBrandingSettings.requestButton')}
                  </button>
                </div>
                {subErr && <p className="text-red-400 text-xs mt-1">{subErr}</p>}
              </Field>
              {form.subdomain && (
                <StatusBadge label={form.subdomain} status={form.subdomainStatus} />
              )}
            </div>
          )}

          {tab === 'email' && (
            <div className="space-y-5">
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-sm text-yellow-300">
                {t('PartnerBrandingSettings.emailDnsInstructionBefore')} <em>no-reply@yourdomain.com</em>{t('PartnerBrandingSettings.emailDnsInstructionAfter')}
              </div>
              <Field label={t('PartnerBrandingSettings.sendingDomainLabel')}>
                <input value={form.sendingDomain || ''} onChange={e => set('sendingDomain', e.target.value)}
                  placeholder="yourfirm.com" className="input-field" />
              </Field>
              {form.sendingDomainVerified
                ? <p className="text-green-400 text-sm">{t('PartnerBrandingSettings.domainVerified')}</p>
                : <p className="text-gray-400 text-sm">{t('PartnerBrandingSettings.domainNotVerified')}</p>
              }
              <div className="bg-white/5 rounded-lg p-4 text-xs font-mono text-gray-300 space-y-2">
                <p className="text-gray-500">{t('PartnerBrandingSettings.addDnsRecords')}</p>
                <p>TXT  @  "v=spf1 include:cataam.com ~all"</p>
                <p>TXT  _dmarc  "v=DMARC1; p=none; rua=mailto:dmarc@cataam.com"</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">
              {saving ? t('PartnerBrandingSettings.saving') : t('PartnerBrandingSettings.saveChanges')}
            </button>
            {saved && <span className="text-green-400 text-sm">{t('PartnerBrandingSettings.savedConfirmation')}</span>}
          </div>
        </div>

        {/* Right: live preview */}
        <div>
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">{t('PartnerBrandingSettings.livePreview')}</p>
          <div className="rounded-xl overflow-hidden border border-white/10" style={{ fontFamily: form.fontFamily || 'Inter' }}>
            {/* Fake nav */}
            <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: previewBg }}>
              {form.logoUrl
                ? <img src={form.logoUrl} alt={t('PartnerBrandingSettings.logoAlt')} className="h-6 object-contain" />
                : <span className="text-white font-bold text-sm">{form.firmName || 'YourFirm'}</span>
              }
              <div className="flex gap-2">
                {[t('PartnerBrandingSettings.navDashboard'), t('PartnerBrandingSettings.navClients'), t('PartnerBrandingSettings.navReports')].map(n => (
                  <span key={n} className="text-white/60 text-xs">{n}</span>
                ))}
              </div>
            </div>
            {/* Fake content */}
            <div className="p-4 bg-gray-900 space-y-3">
              <div className="h-2 rounded w-1/3" style={{ backgroundColor: previewAcc }} />
              <div className="grid grid-cols-3 gap-2">
                {[1,2,3].map(i => (
                  <div key={i} className="h-16 rounded-lg bg-white/5 flex items-center justify-center">
                    <div className="h-2 w-8 rounded" style={{ backgroundColor: previewAcc, opacity: 0.6 }} />
                  </div>
                ))}
              </div>
              <div className="h-24 rounded-lg bg-white/5" />
              {form.whiteLabelTier !== 'HIDDEN' && (
                <p className="text-center text-gray-600 text-xs mt-2">{t('PartnerBrandingSettings.poweredBy')}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function StatusBadge({ label, status }) {
  const colours = {
    NONE: 'text-gray-400 bg-gray-500/10 border-gray-500/20',
    PENDING: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
    VERIFIED: 'text-green-400 bg-green-500/10 border-green-500/20',
    FAILED: 'text-red-400 bg-red-500/10 border-red-500/20',
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${colours[status] || colours.NONE}`}>
      <span>{label}</span>
      <span className="text-xs opacity-70">{status}</span>
    </div>
  );
}
